import {
  put,
  takeLatest,
  fork,
  select,
  takeEvery,
  call,
} from 'redux-saga/effects'

import { setUserForTracking } from 'utils/tracking/set-user-for-tracking'
import { EVENTS, trackEvent } from 'utils/tracking/events-tracker'
import { trackMonthlyExperiment } from 'utils/monthly-experiment'

import {
  getMainUserInfo,
  getMultiUsersInAccount,
  removeMultiUser,
  getUser,
  ipLookUp,
  patchUser,
  closeKwResearchBanner,
  updateUserPreferences,
  getSubscription,
  getAddons,
  closeSmaterThumbPrompt,
  getInvitedUsers,
  deleteInvitedUser,
  getUserLimits,
} from '../../utils/api'
import { signIn, isSubUser, popLoginLimitMessage } from '../../utils/userAuth'
import { PROPERTY_NAMES, PROPERTY_VALUES } from '../../utils/kissmetrics'
import { getCountryCode, getCountryInfo, isCity } from '../../utils/locations'
import { translate } from '../../utils/translation'
import { fetchAlerts } from '../actions/alerts'
import {
  FETCH_USER,
  FETCH_MAIN_USER,
  FETCH_MULTI_USERS,
  DELETE_MULTI_USER,
  fetchUserSuccess,
  UPDATE_USER,
  updateUserSuccess,
  UPDATE_USER_PREFERENCES,
  updateUserPreferencesSuccess,
  updateLifetimeReminderModal,
  updateNavigationChangeModal,
  UPDATE_USER_FAMILIAR_WITH_BANNER,
  UPDATE_USER_SMART_THUMB_POPUP,
  FETCH_SUBSCRIPTION,
  FETCH_USER_LIMITS,
  FETCH_ADDONS,
  fetchSubscriptionSuccess,
  fetchAddonsSuccess,
  fetchAddons,
  fetchSubscription,
  fetchUserLimits,
  fetchMainUserSuccess,
  fetchMainUserFailed,
  fetchMainUser,
  fetchMultiUsers,
  fetchMultiUsersSuccess,
  fetchMultiUsersFailed,
  updateLoginLimitModal,
  FETCH_INVITED_USERS,
  fetchInvitedUsersSuccess,
  fetchInvitedUsersFailed,
  DELETE_INVITED_USER,
  deleteInvitedUserSuccess,
  deleteInvitedUserFailed,
  fetchUserLimitsSuccess,
  updateUserLimits,
} from '../actions/global'
import { selectCurrentUser } from '../selectors'
import { setUserSubscription, setAvailableAddons } from '../../utils/payment'
import { fetchProjects } from '../../containers/Dashboard/actions'
import history from '../../history'
import { skipFetchAfterLoginDataPaths } from '../../utils/consts.mjs'
import { selectCachedLocations } from '../selectors/locationManager'
import { getLocationsInfo } from '../actions/locationManager'
import { updateMetricsLimits } from '../actions/sdManager'
import { Mixpanel, MIXPANEL_PROPS } from '../../utils/mixpanel'

export default function* userSaga() {
  yield fork(loadUser)
  yield takeLatest(FETCH_USER.action, (action) =>
    fetchUser(action.onComplete, action.updateProjects),
  )
  yield takeLatest(FETCH_SUBSCRIPTION.action, loadSubscription)
  yield takeLatest(FETCH_USER_LIMITS.action, loadUserLimits)
  yield takeLatest(FETCH_ADDONS.action, loadAddons)
  yield takeEvery(UPDATE_USER.action, updateUserHandler)
  yield takeLatest(FETCH_MAIN_USER.action, fetchMainUserInfo)
  yield takeLatest(FETCH_MULTI_USERS.action, fetchMultiUsersInfo)
  yield takeLatest(FETCH_INVITED_USERS.action, fetchInvitedUsers)
  yield takeLatest(DELETE_INVITED_USER.action, deleteInvitedUserHandler)
  yield takeEvery(DELETE_MULTI_USER.action, (action) =>
    deleteMultiUserHandler(action.payload, action.onComplete),
  )
  yield takeLatest(
    UPDATE_USER_FAMILIAR_WITH_BANNER.action,
    updateUserFamiliarWithBannerHandler,
  )
  yield takeLatest(
    UPDATE_USER_SMART_THUMB_POPUP.action,
    updateUserSmartThumbHandler,
  )
  yield takeEvery(UPDATE_USER_PREFERENCES.action, updateUserPreferencesHandler)
}

function* loadUser() {
  try {
    const currentUser = yield select(selectCurrentUser())
    const ipResponse = yield call(ipLookUp)
    const { city, country, timezone, regionName } =
      (ipResponse || {}).data || {}
    const countryCode = getCountryCode(ipResponse)

    const userResponse = yield call(getUser, {
      city,
      country,
      countryCode,
      timezone,
      regionName,
    })

    const ipLocation = getCountryInfo(countryCode)
    const preferences = ipLocation
      ? { lang: ipLocation.languageCode, locId: ipLocation.locationId }
      : { lang: 'en', locId: 2840 }
    let user

    if (!userResponse || !userResponse.data) {
      const lifetime_offer_until = userResponse
        ? userResponse.headers['x-lifetime-offer-until']
        : null

      user = {
        isAnnonymous: true,
        countryCode,
        country: ipResponse?.data?.country,
        city: ipResponse?.data?.city,
        regionName,
        subscription: null,
        lifetime_offer_until,
        onboarding: {
          status: 'initial',
        },
        preferences,
        alerts: null,
      }
      Mixpanel.reset()
      Mixpanel.setUserProperties(user)
    } else {
      if (!currentUser || currentUser.id !== userResponse.data.id) {
        setUserForTracking(userResponse.data)
        Mixpanel.setUserProperties({
          ...userResponse.data,
          countryCode,
        })
      }

      user = {
        ...userResponse.data,
        countryCode,
        country: ipResponse?.data?.country,
        city: ipResponse?.data?.city,
        preferences: {
          ...userResponse.data.preferences,
          lang: userResponse.data.preferences?.lang || preferences.lang,
          locId: userResponse.data.preferences?.locId || preferences.locId,
        },
        alerts: undefined,
      }

      const userPreferencesLocId = userResponse.data.preferences?.locId
      const cachedLocations = yield select(selectCachedLocations())
      if (
        userPreferencesLocId &&
        isCity(userPreferencesLocId) &&
        !cachedLocations[userPreferencesLocId]
      ) {
        yield put(getLocationsInfo([userPreferencesLocId]))
      }

      signIn(user)
      const params = new URLSearchParams(window.location.search)

      setTimeout(() => {
        if (params.get('created')) {
          const isMonthlyTier1PriceExperimentEnabled = trackMonthlyExperiment({
            user,
          })

          const wasUserCreated = params.get('created') === 'true'
          const isCheckoutModal = params.get('checkout') === 'true'
          const isEmailLogin = params.get('emailLogin') === 'true'

          const event = wasUserCreated
            ? EVENTS.SIGN_UP_SUCCESS
            : EVENTS.SIGN_IN_SUCCESS

          const signInMethod = isEmailLogin
            ? MIXPANEL_PROPS.SIGN_IN_METHOD.values.EMAIL
            : MIXPANEL_PROPS.SIGN_IN_METHOD.values.GOOGLE

          const kissmetricsLoginType = isEmailLogin
            ? PROPERTY_VALUES.login_type.email
            : PROPERTY_VALUES.login_type.google

          trackEvent({
            event,
            mixpanelProperties: [
              {
                isCheckoutModal,
                signInMethod,
                ...(wasUserCreated && {
                  isAiw: params.get('from') === 'ai_writer',
                }),
                ...(isCheckoutModal && {
                  source:
                    params.get('signup') === 'true'
                      ? MIXPANEL_PROPS.SOURCE.values.ASK_FOR_SIGNUP_MODAL
                      : MIXPANEL_PROPS.SOURCE.values.NEW_CHECKOUT_MODAL,
                }),
                ...(isMonthlyTier1PriceExperimentEnabled && {
                  isMonthlyTier1PriceExperimentEnabled,
                }),
              },
            ],
            gtmProperties: {
              userData: {
                email: user.email,
                externalId: user.id,
              },
              eventId: params.get('eventId'),
            },
            kissmetricsProperties: {
              [PROPERTY_NAMES.login_type]: kissmetricsLoginType,
            },
          })

          params.delete('created')
          params.delete('checkout')
          params.delete('signup')
          params.delete('emailLogin')
          params.delete('eventId')

          history.replace({
            search: params.toString(),
          })
        }
      }, 5000)
    }

    if (
      popLoginLimitMessage() &&
      !window.location.pathname.includes('checkout')
    ) {
      yield put(
        updateLoginLimitModal({
          isOpen: true,
          type: 'LOGIN_LIMIT_REACHED',
          title: translate('login_limit_reached'),
          message: translate('login_limit_reached_text'),
        }),
      )
    }
    yield put(
      updateLifetimeReminderModal({
        isOpen: user.lifetime_unique_offer,
        tier: user.subscription ? user.subscription.tier : null,
      }),
    )
    yield put(
      updateNavigationChangeModal({
        isOpen: user.preferences.nav_change_notified === false,
      }),
    )

    yield put(fetchUserSuccess(user))
    if (
      !skipFetchAfterLoginDataPaths.some((path) =>
        history.location.pathname.endsWith(path),
      )
    ) {
      yield put(fetchProjects())
      yield put(fetchAlerts())
    }
    yield put(fetchUserLimits())
    yield put(fetchSubscription())
    yield put(fetchAddons())
    if (isSubUser(user)) {
      yield put(fetchMainUser())
    }
  } catch (error) {
    console.error(error)
  }
}

function* fetchUser(onComplete, updateProjects = false) {
  const currentUser = yield select(selectCurrentUser())
  const { country, city, countryCode, preferences, regionName } = currentUser
  try {
    const userResponse = yield call(getUser, {
      country,
      city,
      countryCode,
      regionName,
    })

    if (!userResponse || !userResponse.data) {
      const lifetime_offer_until = userResponse
        ? userResponse.headers['x-lifetime-offer-until']
        : null

      const user = {
        isAnnonymous: true,
        countryCode,
        country,
        city,
        regionName,
        subscription: null,
        lifetime_offer_until,
        onboarding: {
          status: 'initial',
        },
        preferences,
      }
      yield put(fetchUserSuccess(user))
    } else {
      const user = {
        ...userResponse.data,
        countryCode,
        regionName,
        preferences: {
          ...userResponse.data.preferences,
          lang: userResponse.data.preferences?.lang || preferences.lang,
          locId: userResponse.data.preferences?.locId || preferences.locId,
        },
      }

      yield put(
        updateLifetimeReminderModal({
          isOpen: user.lifetime_unique_offer,
          tier: user.subscription ? user.subscription.tier : null,
        }),
      )
      yield put(
        updateNavigationChangeModal({
          isOpen: user.preferences.nav_change_notified === false,
        }),
      )
      signIn(user)
      yield put(fetchUserSuccess(user))
      yield put(fetchAddons())
      yield put(fetchSubscription())
      yield put(fetchUserLimits())

      if (isSubUser(user)) {
        yield put(fetchMainUser())
      } else {
        yield put(fetchMultiUsers())
      }

      if (updateProjects) yield put(fetchProjects())

      if (onComplete) onComplete(user)
    }
  } catch (e) {
    const lifetime_offer_until = e.response.headers['x-lifetime-offer-until']

    const user = {
      isAnnonymous: true,
      countryCode,
      country,
      city,
      regionName,
      subscription: null,
      lifetime_offer_until,
      onboarding: {
        status: 'initial',
      },
      preferences,
      alerts: null,
    }

    yield put(fetchUserSuccess(user))
  }
}

function* updateUserHandler(action) {
  try {
    const patchUserResponse = yield patchUser(action.payload)
    yield put(
      updateUserSuccess({
        ...patchUserResponse.data,
      }),
    )
  } catch (error) {
    console.error(error)
  }
}

function* fetchMainUserInfo() {
  try {
    const mainUserResponse = yield getMainUserInfo()
    yield put(
      fetchMainUserSuccess({
        ...mainUserResponse.data.main_user,
        created_at: mainUserResponse.data.created_at,
      }),
    )
  } catch (e) {
    yield put(fetchMainUserFailed(e))
  }
}

function* fetchMultiUsersInfo() {
  try {
    const currentUser = yield select(selectCurrentUser())
    if (!currentUser.isAnnonymous) {
      const multiUsersResponse = yield getMultiUsersInAccount()
      yield put(fetchMultiUsersSuccess(multiUsersResponse.data))
    }
  } catch (e) {
    yield put(fetchMultiUsersFailed(e))
  }
}

function* fetchInvitedUsers() {
  try {
    const currentUser = yield select(selectCurrentUser())
    if (!currentUser.isAnnonymous) {
      const res = yield getInvitedUsers()
      yield put(fetchInvitedUsersSuccess(res.data))
    }
  } catch (e) {
    yield put(fetchInvitedUsersFailed(e))
  }
}

function* deleteInvitedUserHandler(action) {
  try {
    const currentUser = yield select(selectCurrentUser())
    if (!currentUser.isAnnonymous) {
      yield deleteInvitedUser(action.payload)
      yield put(deleteInvitedUserSuccess(action.payload))
    }
  } catch (e) {
    yield put(deleteInvitedUserFailed(action.payload))
  }
}

function* deleteMultiUserHandler(payload, onComplete) {
  try {
    yield removeMultiUser(payload)
    yield fork(fetchMultiUsersInfo)
    onComplete({ data: true })
  } catch (error) {
    console.error(error)
    onComplete({ error })
  }
}

function* updateUserPreferencesHandler(action) {
  try {
    const currentUser = yield select(selectCurrentUser())
    const preferences = currentUser?.preferences || {}
    const preferenceResponse = yield updateUserPreferences(action.payload)
    yield put(
      updateUserPreferencesSuccess({
        ...preferences,
        ...preferenceResponse.data,
      }),
    )
  } catch (error) {
    console.error(error)
  }
}

function* updateUserFamiliarWithBannerHandler() {
  try {
    const user = yield closeKwResearchBanner()
    const currentUser = yield select(selectCurrentUser())

    yield put(
      fetchUserSuccess({
        ...currentUser,
        ...user.data,
      }),
    )
  } catch (error) {
    console.error(error)
  }
}

function* updateUserSmartThumbHandler() {
  try {
    const user = yield closeSmaterThumbPrompt()
    const currentUser = yield select(selectCurrentUser())

    yield put(
      fetchUserSuccess({
        ...currentUser,
        ...user.data,
      }),
    )
  } catch (error) {
    console.error(error)
  }
}

function* loadSubscription() {
  try {
    const currentUser = yield select(selectCurrentUser())
    if (currentUser && currentUser.subscription) {
      let subscription
      const subscriptionResponse = yield getSubscription()

      if (subscriptionResponse && subscriptionResponse.data)
        subscription = subscriptionResponse.data

      setUserSubscription(subscription)
      yield put(fetchSubscriptionSuccess(subscription))
    }
  } catch (e) {
    yield put(fetchSubscriptionSuccess(null))
  }
}

function* loadUserLimits() {
  try {
    const userLimitsResponse = yield getUserLimits()

    let userLimits
    if (userLimitsResponse && userLimitsResponse.data)
      userLimits = userLimitsResponse.data

    yield put(updateUserLimits(userLimits))
    yield put(updateMetricsLimits(userLimits))

    yield put(fetchUserLimitsSuccess(userLimits))
  } catch (error) {
    console.error(error)
  }
}

function* loadAddons() {
  try {
    let addons
    const addonsResponse = yield getAddons()

    if (addonsResponse && addonsResponse.data) addons = addonsResponse.data

    setAvailableAddons(addons)
    yield put(fetchAddonsSuccess(addons))
  } catch (e) {
    yield put(fetchAddonsSuccess({}))
  }
}
